import React, { useEffect, useId, useState } from 'react';
import { ExternalLink, X } from 'lucide-react';
import type { Event } from '../../types';
import { parseIndianMobile } from '../../lib/phone';
import { submitPublicPickupRequest } from '../../lib/events-api';
import { cn } from '../../lib/utils';

const TEST_TURNSTILE_SITE_KEY = '1x0000000000000000000000000000000AA';
const TEST_TURNSTILE_TOKEN = 'XXXX.DUMMY.TOKEN.XXXX';

const accentButtonClass =
  'inline-flex items-center justify-center gap-2 w-full py-3 rounded-[var(--radius-md)] font-medium transition-colors duration-[var(--duration-fast)] bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent-hover)] disabled:opacity-60';

/** Same brand fill as Quick register. Grows to share a row, and wraps onto its own line when the card is narrow. */
const collectionButtonClass =
  'inline-flex min-h-11 flex-1 basis-[11rem] items-center justify-center gap-2 whitespace-normal px-3 py-3 text-center rounded-[var(--radius-md)] font-medium transition-colors duration-[var(--duration-fast)] bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent-hover)]';

const inputClass =
  'w-full bg-[var(--color-surface-1)] border border-[var(--color-border)] rounded-[var(--radius-md)] py-2.5 px-3.5 text-[var(--color-text-primary)] placeholder:text-[var(--color-text-tertiary)] outline-none focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent-subtle)] transition-all duration-[var(--duration-fast)] text-sm';

export function hasDropPoints(event: Event): boolean {
  return Array.isArray(event.dropPoints) && event.dropPoints.some((point) => point.address?.trim());
}

export function offersPickup(event: Event): boolean {
  return event.pickupEnabled === true && Boolean(event.slug?.trim());
}

function turnstileSiteKey(): string {
  const configured = import.meta.env.VITE_TURNSTILE_SITE_KEY?.trim() ?? '';
  if (configured) return configured;
  if (import.meta.env.DEV) return TEST_TURNSTILE_SITE_KEY;
  return '';
}

function TurnstileField({ onToken }: { onToken: (token: string) => void }) {
  const siteKey = turnstileSiteKey();
  const holder = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!siteKey || !holder.current) return;
    let widgetId: string | null = null;
    let cancelled = false;
    const render = () => {
      if (cancelled || !holder.current || !window.turnstile) return;
      widgetId = window.turnstile.render(holder.current, {
        sitekey: siteKey,
        theme: 'light',
        callback: (token) => onToken(token),
        'error-callback': () => onToken(''),
      });
    };
    if (window.turnstile) {
      render();
    } else {
      const existing = document.querySelector<HTMLScriptElement>('script[data-turnstile]');
      if (existing) {
        existing.addEventListener('load', render);
      } else {
        const script = document.createElement('script');
        script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
        script.async = true;
        script.defer = true;
        script.dataset.turnstile = 'true';
        script.onload = render;
        document.head.appendChild(script);
      }
    }
    return () => {
      cancelled = true;
      if (widgetId && window.turnstile) window.turnstile.remove(widgetId);
    };
  }, [onToken, siteKey]);

  if (!siteKey) return null;
  return <div ref={holder} className="min-h-16" />;
}

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        opts: {
          sitekey: string;
          theme?: string;
          callback?: (token: string) => void;
          'error-callback'?: () => void;
        },
      ) => string;
      remove: (id: string) => void;
    };
  }
}

export function EventCollectionButtons({
  event,
  onDropPoints,
  onPickup,
}: {
  event: Event;
  onDropPoints: () => void;
  onPickup: () => void;
}) {
  const drop = hasDropPoints(event);
  const pickup = offersPickup(event);
  if (!drop && !pickup) return null;
  return (
    <div className="flex w-full flex-wrap gap-2">
      {drop && (
        <button type="button" onClick={onDropPoints} className={collectionButtonClass}>
          Drop points
        </button>
      )}
      {pickup && (
        <button type="button" onClick={onPickup} className={collectionButtonClass}>
          Request pickup
        </button>
      )}
    </div>
  );
}

function DialogShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  useEffect(() => {
    const onKey = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') {
        ev.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-end justify-center bg-[oklch(18%_0.01_55/0.72)] p-4 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-lg max-h-[calc(100vh-2rem)] overflow-y-auto rounded-[var(--radius-lg)] bg-[var(--color-surface-1)] p-5 shadow-[var(--shadow-xl)]"
        onClick={(ev) => ev.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <h2 className="text-lg font-semibold text-[var(--color-text-primary)]">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-2 text-[var(--color-text-secondary)] hover:bg-[var(--color-surface-2)]"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function EventDropPointsDialog({ event, onClose }: { event: Event; onClose: () => void }) {
  const points = (event.dropPoints ?? []).filter((point) => point.address?.trim());
  return (
    <DialogShell title="Drop points" onClose={onClose}>
      <ol className="space-y-3">
        {points.map((point, index) => (
          <li
            key={`${point.address}-${index}`}
            className="rounded-[var(--radius-md)] border border-[var(--color-border)] p-3 space-y-1"
          >
            {point.label?.trim() && (
              <p className="text-sm font-semibold text-[var(--color-text-primary)]">{point.label}</p>
            )}
            <p className="text-sm text-[var(--color-text-secondary)] whitespace-pre-line">{point.address}</p>
            {point.mapsUrl && (
              <a
                href={point.mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm font-medium text-[var(--color-accent)] mt-1"
              >
                Open in Google Maps
                <ExternalLink className="w-4 h-4" />
              </a>
            )}
          </li>
        ))}
      </ol>
    </DialogShell>
  );
}

export function PickupRequestFormView({
  phase,
  error,
  name,
  phone,
  address,
  company,
  submitting,
  onName,
  onPhone,
  onAddress,
  onCompany,
  onToken,
  onSubmit,
}: {
  phase: 'form' | 'success';
  error: string | null;
  name: string;
  phone: string;
  address: string;
  company: string;
  submitting: boolean;
  onName: (value: string) => void;
  onPhone: (value: string) => void;
  onAddress: (value: string) => void;
  onCompany: (value: string) => void;
  onToken: (token: string) => void;
  onSubmit: () => void;
}) {
  const nameId = useId();
  const phoneId = useId();
  const addressId = useId();

  if (phase === 'success') {
    return (
      <div role="status" className="space-y-2">
        <p className="text-sm font-medium text-[var(--color-text-primary)]">Pickup request received.</p>
        <p className="text-sm text-[var(--color-text-secondary)]">
          {name ? `Request saved for ${name}. ` : ''}
          The organisers will use the number and address you gave.
        </p>
      </div>
    );
  }

  return (
    <form
      className="space-y-3"
      onSubmit={(ev) => {
        ev.preventDefault();
        onSubmit();
      }}
    >
      {error && (
        <p role="alert" className="text-sm text-[var(--color-error-text)] bg-[var(--color-error-subtle)] rounded-[var(--radius-md)] px-3 py-2">
          {error}
        </p>
      )}
      <label className="block" htmlFor={nameId}>
        <span className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1.5">Name</span>
        <input
          id={nameId}
          name="name"
          type="text"
          required
          autoComplete="name"
          className={inputClass}
          value={name}
          onChange={(ev) => onName(ev.target.value)}
        />
      </label>
      <label className="block" htmlFor={phoneId}>
        <span className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1.5">Phone number</span>
        <input
          id={phoneId}
          name="phone"
          type="tel"
          inputMode="numeric"
          required
          autoComplete="tel"
          placeholder="10-digit mobile"
          className={inputClass}
          value={phone}
          onChange={(ev) => onPhone(ev.target.value)}
        />
      </label>
      <label className="block" htmlFor={addressId}>
        <span className="block text-xs font-medium text-[var(--color-text-secondary)] mb-1.5">Address</span>
        <textarea
          id={addressId}
          name="address"
          required
          rows={4}
          autoComplete="street-address"
          className={cn(inputClass, 'resize-y')}
          value={address}
          onChange={(ev) => onAddress(ev.target.value)}
        />
      </label>
      <div className="absolute -left-[9999px] h-px w-px overflow-hidden" aria-hidden="true">
        <label>
          Company
          <input
            name="company"
            tabIndex={-1}
            autoComplete="off"
            value={company}
            onChange={(ev) => onCompany(ev.target.value)}
          />
        </label>
      </div>
      <TurnstileField onToken={onToken} />
      <button type="submit" className={accentButtonClass} disabled={submitting}>
        {submitting ? 'Sending…' : 'Request pickup'}
      </button>
    </form>
  );
}

export function EventPickupRequestDialog({ event, onClose }: { event: Event; onClose: () => void }) {
  const [phase, setPhase] = useState<'form' | 'success'>('form');
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [company, setCompany] = useState('');
  const [token, setToken] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async () => {
    const phoneParsed = parseIndianMobile(phone);
    if (name.trim().length < 2) {
      setError('Enter your full name.');
      return;
    }
    if (!phoneParsed.ok) {
      setError(phoneParsed.error ?? 'Enter a valid 10-digit Indian mobile number');
      return;
    }
    if (address.trim().length < 5) {
      setError('Enter the pickup address.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const turnstileToken =
        token || (import.meta.env.DEV && !import.meta.env.VITE_TURNSTILE_SITE_KEY ? TEST_TURNSTILE_TOKEN : '');
      await submitPublicPickupRequest(event.slug, {
        name,
        phone,
        address,
        company,
        turnstileToken,
      });
      setPhase('success');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send the pickup request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <DialogShell title="Request pickup" onClose={onClose}>
      <p className="text-sm text-[var(--color-text-secondary)] mb-4">{event.title}</p>
      <PickupRequestFormView
        phase={phase}
        error={error}
        name={name}
        phone={phone}
        address={address}
        company={company}
        submitting={submitting}
        onName={setName}
        onPhone={setPhone}
        onAddress={setAddress}
        onCompany={setCompany}
        onToken={setToken}
        onSubmit={() => void onSubmit()}
      />
    </DialogShell>
  );
}
